import PropTypes from "prop-types"
import { useMemo } from "react"
import moment from "moment"
import ReactApexChart from "react-apexcharts"
import { Card, CardBody, Input } from "reactstrap"

import { buildMonthlyDailySalesSeries, formatMonthLabel } from "../dashboard-data"
import { getDailySalesSummary } from "services/tickets"
import useDashboardQuery from "../useDashboardQuery"

const DashboardCashChart = ({ selectedMonth, onSelectedMonthChange }) => {
  const selectedMonthDate = moment(selectedMonth || moment().format("YYYY-MM"), "YYYY-MM")

  const dailySalesSummaryQuery = useDashboardQuery(
    ["dashboard-daily-sales-summary", selectedMonth],
    () =>
      getDailySalesSummary({
        month: selectedMonthDate.month() + 1,
        year: selectedMonthDate.year(),
      }),
    { keepPreviousData: true, staleTime: 1000 * 60 * 15 }
  )

  const cashSeriesData = useMemo(
    () => buildMonthlyDailySalesSeries(dailySalesSummaryQuery.data || [], selectedMonthDate.format("YYYY-MM")),
    [dailySalesSummaryQuery.data, selectedMonthDate]
  )

  const cashSalesChartOptions = useMemo(
    () => ({
      chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
      colors: ["#14b8a6"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 3 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 90, 100] },
      },
      grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      xaxis: {
        categories: cashSeriesData.labels,
        labels: { style: { colors: "#64748b", fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter: (value) =>
            new Intl.NumberFormat("es-MX", {
              style: "currency",
              currency: "MXN",
              maximumFractionDigits: 0,
            }).format(value),
          style: { colors: "#64748b" },
        },
      },
      tooltip: {
        y: {
          formatter: (value) =>
            new Intl.NumberFormat("es-MX", {
              style: "currency",
              currency: "MXN",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value),
        },
      },
      legend: { show: false },
    }),
    [cashSeriesData.labels]
  )

  const cashSalesChartSeries = useMemo(
    () => [
      {
        name: "Efectivo de ventas",
        data: cashSeriesData.data,
      },
    ],
    [cashSeriesData.data]
  )

  return (
    <Card className="shadow-sm border-0 h-100">
      <CardBody className="p-4">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
          <div>
            <h5 className="mb-1">Ventas del mes</h5>
          </div>
        </div>

        <div className="list-filter-bar mb-3">
          <div className="list-filter-group list-filter-date-group" aria-label="Filtrar por mes">
            <span className="list-filter-icon">
              <i className="fa fa-calendar" />
            </span>
            <div className="list-filter-range">
              <Input
                type="month"
                bsSize="sm"
                value={selectedMonth}
                onChange={(event) => onSelectedMonthChange(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <small className="text-muted">Mes seleccionado: {formatMonthLabel(selectedMonth)}</small>
        </div>

        <div style={{ minHeight: 340 }}>
          <ReactApexChart options={cashSalesChartOptions} series={cashSalesChartSeries} type="area" height={340} />
        </div>
      </CardBody>
    </Card>
  )
}

DashboardCashChart.propTypes = {
  selectedMonth: PropTypes.string.isRequired,
  onSelectedMonthChange: PropTypes.func.isRequired,
}

export default DashboardCashChart